/**
 * Shared, lazily-installed tracker for global pointer and scroll activity.
 *
 * `VideoPreview` uses this to gate hover playback on the pointer actually
 * *settling* over a card rather than merely being present for a fixed time.
 * This is what distinguishes an intentional hover from:
 *   - a fast pointer moving across the grid on the way elsewhere (it keeps
 *     firing `pointermove`, so it never settles), and
 *   - the page scrolling under a stationary pointer (cards pass beneath and
 *     fire enter/leave with no pointer movement — but `scroll` keeps firing,
 *     so we still treat it as "not settled").
 */

let lastPointerMoveAt = Number.NEGATIVE_INFINITY;
let lastScrollAt = Number.NEGATIVE_INFINITY;
let installed = false;

const handlePointerMove = () => {
	lastPointerMoveAt = performance.now();
};

const handleScroll = () => {
	lastScrollAt = performance.now();
};

/** Install the global listeners once (idempotent, safe to call repeatedly). */
export function ensurePointerActivityTracking(): void {
	if (installed || typeof document === "undefined") {
		return;
	}
	installed = true;
	// Seed with "just now" so the first hover (before any pointermove has been
	// observed) still waits for a genuine settle rather than firing immediately.
	lastPointerMoveAt = performance.now();
	// Passive + capture so we observe every move/scroll without interfering with
	// other handlers. `scroll` doesn't bubble, so capture is required to catch
	// scrolling on any nested scroller.
	document.addEventListener("pointermove", handlePointerMove, {
		passive: true,
		capture: true,
	});
	document.addEventListener("scroll", handleScroll, {
		passive: true,
		capture: true,
	});
}

/**
 * Milliseconds since the pointer last moved *or* the page last scrolled —
 * i.e. how long the pointer has been genuinely settled over its target.
 */
export function msSincePointerSettled(): number {
	return performance.now() - Math.max(lastPointerMoveAt, lastScrollAt);
}
